import Vue from "vue";
import Meta from "vue-meta";
import Router from "vue-router";

import { set as setAnalytics } from "vue-analytics";

import store from "@/store";
import versionService from "@/common/version.service";

import { routes } from "@/router/routes/subdomain.wordfinder";

import {
	GAME_TYPES,
	PAGES,
	DEFAULT_WORDLENGTH,
	VERSION_CHECK_ERROR,
	SITE_LANGUAGE,
	DICTIONARIES,
} from "@/common/consts";

import {
	isTilesInBlacklist,
	alphabetizeTiles,
	isScrabbleDictionaryPage,
	sanitizeTiles,
	resultsPageURL,
	readDictionary,
	readFilter,
	getDefaultDictionariesByPage,
	hasEndingSlashInPath,
	getAdLayout,
	getPageTemplateValue,
} from "@/common/functions";

Vue.use(Router);
Vue.use(Meta);

const router = new Router({
	mode: "history",
	routes,
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) return savedPosition;
		return { x: 0, y: 0 };
	},
});

/** Vue Router beforeEach Navigation Guard */
router.beforeEach((to, from, next) => {
	// 0.1. Remove all earlier ad slots.
	if (
		typeof window.__PRERENDER_INJECTED === "undefined" &&
		typeof window.__TEST__ === "undefined"
	) {
		if (window.htlbid) {
			window.htlbid.cmd.push(function() {
				window.htlbid.removeSlot();

				[...document.getElementsByClassName("wordfinder-ad")].forEach(
					(el) => {
						el.innerHTML = "";
					}
				);
			});
		}
	}

	// 1. Close burger menu if needed
	store.dispatch("closeBurger");

	// 2. Check if we need to modify state due to URL

	store.commit("updateExact", false);
	store.commit("updatePrefixedTiles", "");

	switch (to.name) {
		case PAGES.SCRABBLE_EXACTMATCH_RESULTS:
		case PAGES.SCRABBLE_DICT_RESULTS:
			store.commit("updateWordlength", null);
			break;
		case PAGES.FOURPICS:
			store.commit("updateWordlength", DEFAULT_WORDLENGTH);
			break;
		case PAGES.FOURPICS_RESULTS:
			store.commit(
				"updateWordlength",
				parseInt(to.params.wordlength) || DEFAULT_WORDLENGTH
			);
			break;
		case PAGES.SCRABBLE_DICT:
		case PAGES.SCRABBLE_CHECKER:
			store.commit("setDictionary", DICTIONARIES[2]);
			break;
		case PAGES.UNSCRAMBLE:
		case PAGES.WORD_DESCRAMBLER:
		case PAGES.WORD_UNSCRAMBLER:
			store.commit("setDictionary", DICTIONARIES[2]);
			break;
		case PAGES.WORDFEUD:
		case PAGES.WORD_CONNECT:
		case PAGES.WORDBRAIN_ANSWERS:
		case PAGES.WORD_STORY:
			store.commit("setDictionary", DICTIONARIES[4]);
			break;
		case PAGES.WORD_CHUMS_CHEAT:
			store.commit("setDictionary", DICTIONARIES[6]);
			break;
		case PAGES.WWF:
			store.commit("setDictionary", DICTIONARIES[1]);
			break;
		case PAGES.WORDSCAPES:
			store.commit("setDictionary", DICTIONARIES[5]);
			break;
	}

	/** 3. Normalize destination URL */

	// 3.1 For Unscramble-like result pages
	if (typeof to.params.tiles !== "undefined") {
		let tiles = to.params.tiles;

		store.commit("updateTiles", {
			tiles,
			routeName: to.name,
		});

		let normalizedTiles;
		if (isScrabbleDictionaryPage(to.name)) {
			normalizedTiles = store.getters.getTiles;
		} else {
			normalizedTiles = sanitizeTiles(tiles, to.name, true);
			if (isTilesInBlacklist(normalizedTiles.toLowerCase())) {
				normalizedTiles = alphabetizeTiles(normalizedTiles, to.name);
			}
		}

		if (tiles !== normalizedTiles) {
			store.commit("updateUnsortedTiles", {
				tiles,
				routeName: to.name,
			});
			const path = decodeURIComponent(to.path).replace(
				to.params.tiles,
				normalizedTiles
			);
			router.replace(path);
		} else {
			const gameType =
				to.name == PAGES.FOURPICS_RESULTS
					? GAME_TYPES.FOUR_PICS
					: to.name;

			if (to.name === PAGES.FOURPICS_RESULTS) {
				const dictionary = readDictionary(
					getDefaultDictionariesByPage(to.name)
				);
				const bonus = readFilter().length
					? readFilter()[0].bonuspoints
					: false;
				store.dispatch("executeQuery", {
					anagram: true,
					gameType,
					dictionary,
					bonus,
				});
			}
		}
	}

	// 3.2. For word lists URLs
	if (typeof to.params.letter !== "undefined") {
		// Make the letter lowercase.
		const letter = to.params.letter
			.toLowerCase()
			.replace(/[^a-z\-_]/gi, "");
		if (letter !== to.params.letter) {
			const path = decodeURIComponent(to.path).replace(
				to.params.letter,
				letter
			);
			next(path);
			return;
		}
	}

	// 3.3. For letter words URLs
	if (
		typeof to.params.length !== "undefined" &&
		typeof to.params.length.replace === "function"
	) {
		let length = to.params.length.replace(/[^\d\-\w]/g, "");

		if (
			!length.match(/^\d+(-with-)?/) &&
			!length.match(/^\d+(-starts-)?/) &&
			!length.match(/^\d+(-ends-)?/)
		) {
			length = "";
		}

		if (parseInt(length) < 2) length = "2";

		const maxLimit = 20;
		if (parseInt(length) > maxLimit) length = maxLimit.toString();

		if (length !== to.params.length) {
			const path = decodeURIComponent(to.path).replace(
				to.params.length,
				length
			);
			next(path);
			return;
		}
	}

	// 3.4. Recover from non Vue-routed GET requests
	if (
		typeof to.params.tiles === "undefined" &&
		typeof to.query.tiles === "string"
	) {
		const path = resultsPageURL(to.query.currentSection);
		const tiles = `/${sanitizeTiles(to.query.tiles, to.name)}`;

		let params = "";
		if (
			typeof to.query.remember !== "undefined" &&
			to.query.remember === "on"
		) {
			params = `/remember_tiles`;
		}
		if (typeof to.query.wordlength !== "undefined") {
			params = `/${parseInt(to.query.wordlength)}`;
		}

		const fixedURL = `${path}${params}${tiles}`;
		next(fixedURL);
		return;
	}

	// 4. Add trailing slash where necessary
	if (!hasEndingSlashInPath(to.path)) {
		next(`${to.path}/`);
		return;
	}

	// 5. Set the content grouping.
	if (
		typeof window.__PRERENDER_INJECTED === "undefined" &&
		typeof window.__TEST__ === "undefined"
	) {
		setAnalytics("contentGroup1", getPageTemplateValue(to.name));
	}

	store.commit("updateCurrentPage", to.name);
	next();
});

/** Vue Router afterEach Navigation Guard */
router.afterEach((to, from) => {
	if (
		typeof window.__PRERENDER_INJECTED === "undefined" &&
		typeof window.__TEST__ === "undefined"
	) {
		// 0. Check if App has been updated and force reload if that's the case
		versionService.check().catch((err) => {
			if (err.name === VERSION_CHECK_ERROR) {
				window.location.reload(true);
			}
		});
		window.$ltkAnalytics.page({
			type: "load",
			path: to.fullPath,
			url: window.location.hostname + to.fullPath,
		});

		// 1. Signal HTL to render a new layout.
		if (window.htlbid) {
			window.htlbid.cmd.push(function() {
				window.htlbid.layout(getAdLayout(to.name));
			});
		}
	}
});

export default router;
